/**
 * Pure bookkeeping for the in-flight operation list the controller keeps
 * beside the reducer (SE-8389): what a later move rebases over, and which
 * entries a settled request removes.
 * @module features/scouting/hooks/watchlistPreferenceOrderQueue
 */
import type { PitchFormation, TacticalSlot } from "@/generated/graphql";

import { PITCH_FORMATION_ROWS } from "@/features/scouting/constants/pitchFormationRows";

import {
  applyRelativePreferenceOperation,
  isPitchAssignmentOperation,
  isPitchFormationOperation,
  type WatchlistPitchOperation,
  type WatchlistPreferenceSnapshot,
} from "./watchlistPreferenceOrderState";

/**
 * Complete slot list of one formation, in field order. Shared with the field
 * renderer so the optimistic migration and the drawn pitch agree by
 * construction.
 * @param formation - Formation identity
 * @returns Every tactical slot the formation renders
 */
export const formationSlots = (
  formation: PitchFormation
): readonly TacticalSlot[] => (PITCH_FORMATION_ROWS[formation] ?? []).flat();

/** The request behind one operation, re-runnable against a later revision. */
export interface PitchWriteExecutor {
  readonly execute: (
    confirmed: WatchlistPreferenceSnapshot
  ) => Promise<WatchlistPreferenceSnapshot>;
}

/**
 * Removes a settled operation from the in-flight list. A server snapshot
 * supersedes the operation and every older one, as the reducer slices them;
 * a lone settle (Discard, a refused re-apply) removes just that one.
 * @param pending - In-flight operations, oldest first
 * @param operationId - The operation that settled
 * @param through - Whether older operations settled with it
 * @returns The remaining in-flight operations
 */
export const settlePendingOperations = (
  pending: readonly WatchlistPitchOperation[],
  operationId: string,
  through: boolean
): readonly WatchlistPitchOperation[] => {
  const index = pending.findIndex(item => item.id === operationId);
  return through && index >= 0
    ? pending.slice(index + 1)
    : pending.filter(item => item.id !== operationId);
};

/**
 * The OLDER operations a server snapshot for `operationId` supersedes — the
 * ones whose requests no longer matter and whose executors can be released.
 * The settled operation itself is not listed: after a conflict its executor
 * still serves Re-apply.
 * @param pending - In-flight operations, oldest first
 * @param operationId - The operation whose snapshot landed
 * @param through - Whether older operations settled with it; none otherwise
 * @returns Ids of the operations older than it
 */
export const supersededOperationIds = (
  pending: readonly WatchlistPitchOperation[],
  operationId: string,
  through = true
): readonly string[] => {
  const index = pending.findIndex(item => item.id === operationId);
  return !through || index < 0
    ? []
    : pending.slice(0, index).map(item => item.id);
};

/**
 * The order a new move is expressed against: the confirmed order with every
 * in-flight reorder replayed — including one kept on screen after failing,
 * so the next save carries that change along instead of contradicting it.
 * @param confirmedOrder - Last authoritative order
 * @param pending - In-flight operations, oldest first
 * @returns The effective order the next operation rebases over
 */
export const pendingOperationBase = (
  confirmedOrder: readonly string[],
  pending: readonly WatchlistPitchOperation[]
): readonly string[] =>
  pending.reduce<readonly string[]>(
    (order, pendingOperation) =>
      isPitchAssignmentOperation(pendingOperation) ||
      isPitchFormationOperation(pendingOperation)
        ? order
        : applyRelativePreferenceOperation(order, pendingOperation),
    confirmedOrder
  );
